import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../config/api';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';
import { trackEvent } from '../utils/analytics';
import { buildTradingViewUrl } from '../utils/tradingViewUrl';

const Opportunities = () => {
  // Configuration: Age limit in minutes for greying out old opportunities
  const OPPORTUNITY_AGE_LIMIT_MINUTES = 18;

  const { user } = useAuth();
  const userId = user?.id || null; // Get userId from auth context, null if not logged in

  const [opportunities, setOpportunities] = useState([]);
  const [filteredOpportunities, setFilteredOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    strategy: '',
    symbol: '',
    ex1: '',
    ex2: ''
  });
  const [profitFilter, setProfitFilter] = useState({
    operator: 'higher',
    value: ''
  });
  const [ageFilter, setAgeFilter] = useState(7); // Default to 7 days
  const [sortBy, setSortBy] = useState({
    column: 'final_profit',
    direction: '9-1'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100); // Default 100 groups per page
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalGroups, setTotalGroups] = useState(0); // Total number of opportunity groups
  const [expandedGroups, setExpandedGroups] = useState(new Set()); // Track which groups are expanded
  const [expandedMobileRow, setExpandedMobileRow] = useState(null); // Track which row is expanded on mobile
  const [currentTime, setCurrentTime] = useState(new Date()); // For countdown timer
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768); // Track if mobile view

  // Multi-select state for bulk actions
  const [selectedOpportunityIds, setSelectedOpportunityIds] = useState([]); // Array of selected group IDs
  const [bulkAction, setBulkAction] = useState(''); // Current bulk action selected

  useEffect(() => {
    fetchOpportunities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, ageFilter, userId]);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, opportunities, sortBy, profitFilter]);

  // Update countdown every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get unique strategy values for dropdown
  const getUniqueStrategies = () => {
    const strategies = opportunities
      .map(opp => opp.strategy)
      .filter(strategy => strategy && strategy.trim() !== '')
      .filter((strategy, index, arr) => arr.indexOf(strategy) === index)
      .sort();
    return ['', ...strategies];
  };


  // Get unique exchange values for dropdown
  const getUniqueExchanges = () => {
    const exchanges = [];
    opportunities.forEach(opp => {
      if (opp.ex1_name && opp.ex1_name.trim() !== '') exchanges.push(opp.ex1_name);
      if (opp.ex2_name && opp.ex2_name.trim() !== '') exchanges.push(opp.ex2_name);
    });
    const uniqueExchanges = [...new Set(exchanges)].sort();
    return ['', ...uniqueExchanges];
  };

  const applyFilters = () => {
    let filtered = opportunities;

    // Apply age filter - already done in backend, but keep for client-side filtering
    if (ageFilter && !isNaN(ageFilter) && ageFilter > 0) {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - ageFilter);

      filtered = filtered.filter(opp => {
        if (!opp.timestamp) return false;
        const oppDate = new Date(opp.timestamp);
        return oppDate >= cutoffDate;
      });
    }

    if (filters.strategy) {
      filtered = filtered.filter(opp =>
        opp.strategy === filters.strategy
      );
    }

    if (filters.symbol) {
      filtered = filtered.filter(opp =>
        opp.symbol?.toLowerCase().includes(filters.symbol.toLowerCase())
      );
    }

    if (filters.ex1) {
      filtered = filtered.filter(opp =>
        opp.ex1_name === filters.ex1
      );
    }

    if (filters.ex2) {
      filtered = filtered.filter(opp =>
        opp.ex2_name === filters.ex2
      );
    }

    // Apply profit filter
    if (profitFilter.value && profitFilter.value.trim() !== '') {
      const filterValue = parseFloat(profitFilter.value.replace(',', '.'));
      if (!isNaN(filterValue)) {
        filtered = filtered.filter(opp => {
          const profit = parseFloat(opp.final_profit);
          if (isNaN(profit)) return false;

          if (profitFilter.operator === 'higher') {
            return profit > filterValue;
          } else {
            return profit < filterValue;
          }
        });
      }
    }

    // Group by group_id and sort by timestamp within each group
    const groupedByGroupId = {};
    filtered.forEach(opp => {
      const groupId = opp.group_id || opp.id; // Use record ID if no group_id
      if (!groupedByGroupId[groupId]) {
        groupedByGroupId[groupId] = [];
      }
      groupedByGroupId[groupId].push(opp);
    });

    // Sort records within each group by timestamp (newest first)
    Object.keys(groupedByGroupId).forEach(groupId => {
      groupedByGroupId[groupId].sort((a, b) => {
        const dateA = new Date(a.timestamp || 0);
        const dateB = new Date(b.timestamp || 0);
        return dateB - dateA; // Newest first
      });
    });

    // Create flat array with newest record from each group for sorting
    const newestRecords = Object.keys(groupedByGroupId).map(groupId => ({
      groupId: groupId,
      records: groupedByGroupId[groupId],
      newest: groupedByGroupId[groupId][0]
    }));

    // Apply sorting to the newest records
    newestRecords.sort((a, b) => {
      let valueA, valueB;

      switch (sortBy.column) {
        case 'strategy':
          valueA = a.newest.strategy || '';
          valueB = b.newest.strategy || '';
          break;
        case 'symbol':
          valueA = a.newest.symbol || '';
          valueB = b.newest.symbol || '';
          break;
        case 'final_profit':
          valueA = parseFloat(a.newest.final_profit) || 0;
          valueB = parseFloat(b.newest.final_profit) || 0;
          break;
        case 'timestamp':
          valueA = new Date(a.newest.timestamp || 0);
          valueB = new Date(b.newest.timestamp || 0);
          break;
        case 'ex1_name':
          valueA = a.newest.ex1_name || '';
          valueB = b.newest.ex1_name || '';
          break;
        case 'ex2_name':
          valueA = a.newest.ex2_name || '';
          valueB = b.newest.ex2_name || '';
          break;
        default:
          valueA = a.newest.final_profit || 0;
          valueB = b.newest.final_profit || 0;
      }

      let comparison = 0;
      if (typeof valueA === 'string') {
        comparison = valueA.localeCompare(valueB);
      } else {
        comparison = valueA - valueB;
      }

      return sortBy.direction === '1-9' ? comparison : -comparison;
    });

    // Store the grouped structure for rendering
    setFilteredOpportunities(newestRecords);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));

    // Track filter usage
    trackEvent('Opportunities', 'Filter Applied', `${field}: ${value || 'cleared'}`);
  };

  const toggleExpandGroup = (groupId) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const toggleMobileRow = (groupId) => {
    setExpandedMobileRow(expandedMobileRow === groupId ? null : groupId);
  };

  const clearFilters = () => {
    setFilters({
      strategy: '',
      symbol: '',
      ex1: '',
      ex2: ''
    });
    setProfitFilter({
      operator: 'higher',
      value: ''
    });
    setAgeFilter(7); // Reset to default 7 days
    setSortBy({
      column: 'final_profit',
      direction: '9-1'
    });

    // Track clear filters action
    trackEvent('Opportunities', 'Filters Cleared', 'All filters reset');
  };

  const setBestOpportunitiesPreset = () => {
    setFilters({
      strategy: '',
      symbol: '',
      ex1: '',
      ex2: ''
    });
    setProfitFilter({
      operator: 'higher',
      value: ''
    });
    setSortBy({
      column: 'final_profit',
      direction: '1-9'
    });

    // Track preset applied
    trackEvent('Opportunities', 'Preset Applied', 'Best Opportunities (by profit, descending)');
  };

  const fetchOpportunities = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query parameters
      const params = new URLSearchParams({
        page: currentPage,
        pageSize: pageSize === 'unlimited' ? 'unlimited' : pageSize,
        ageDays: ageFilter || 7
      });

      // Add userId if logged in
      if (userId && userId !== 0) {
        params.append('userId', userId);
      }

      const response = await fetch(`${API_BASE_URL}/api/opportunities?${params}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setOpportunities(data.data);
        setFilteredOpportunities(data.data);
        setTotalRecords(data.totalRecords || 0);
        setTotalGroups(data.totalGroups || 0);
      } else {
        throw new Error(data.error || 'Failed to fetch opportunities');
      }

      setLoading(false);

    } catch (err) {
      console.error('Error fetching opportunities:', err);
      setError('Failed to fetch opportunities data');
      setOpportunities([]);
      setFilteredOpportunities([]);
      setLoading(false);
    }
  };

  // Calculate next funding time (funding happens every 8 hours: 00:00, 08:00, 16:00 UTC)
  // If nextFundingTimeFromDB is provided, use that value instead of calculating
  const getNextFundingTime = (nextFundingTimeFromDB) => {
    // If we have the next funding time from DB, use it
    if (nextFundingTimeFromDB) {
      return new Date(nextFundingTimeFromDB);
    }

    // Otherwise, fall back to calculating it (for backwards compatibility)
    const now = new Date();
    const nextFunding = new Date(now);

    // Get current hour in UTC
    const currentHourUTC = now.getUTCHours();

    // Funding times in UTC: 00:00, 08:00, 16:00
    let nextFundingHour;
    if (currentHourUTC < 8) {
      nextFundingHour = 8;
    } else if (currentHourUTC < 16) {
      nextFundingHour = 16;
    } else {
      // After 16:00, next funding is tomorrow at 00:00
      nextFundingHour = 0;
      nextFunding.setUTCDate(nextFunding.getUTCDate() + 1);
    }

    nextFunding.setUTCHours(nextFundingHour, 0, 0, 0);
    return nextFunding;
  };

  // Format countdown as hh:mm:ss and return color based on time remaining
  const formatCountdown = (futureDate) => {
    if (!futureDate) return { text: '--:--:--', color: '#8b949e' };

    const now = currentTime;
    const diff = futureDate - now;

    if (diff <= 0) return { text: '00:00:00', color: '#8b949e' };

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    const totalMinutes = hours * 60 + minutes;

    // Determine color based on time remaining
    let color;
    if (totalMinutes >= 60) {
      color = '#3fb950'; // Green (more than 1 hour)
    } else if (totalMinutes >= 30) {
      color = '#ff9800'; // Orange (30 min to 1 hour)
    } else {
      color = '#ff6b6b'; // Light red (less than 30 min)
    }

    return {
      text: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`,
      color: color
    };
  };

  // Format next funding time for display as YYYY-MM-DD hh:mm
  const formatNextFundingTime = (date) => {
    if (!date) return '-';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  const formatRate = (rate) => {
    if (rate === null || rate === undefined) return '-';
    const value = (Number(rate) * 100).toFixed(6);
    return `${value}%`;
  };

  // Extract contract types from strategy string.
  // Supports old format (e.g., "SH1SP2") and new format with Fxx prefix (e.g., "F11SH1SP1", "F10SH1LO2", "F01SH1LO2").
  const getContractTypesFromStrategy = (strategy) => {
    if (!strategy) return ['SPOT', 'SPOT'];

    const strategyUpper = strategy.toUpperCase();
    let ex1Contract = 'SPOT';
    let ex2Contract = 'SPOT';

    // New format: Fxx prefix (F11, F10, F01) — skip 3 chars before contract codes
    const offset = /^F\d\d/.test(strategyUpper) ? 3 : 0;

    // Extract first contract type (EX1: SH=SHORT, LO=LONG, SP=SPOT)
    const ex1Code = strategyUpper.substring(offset, offset + 2);
    if (ex1Code === 'LO') {
      ex1Contract = 'LONG';
    } else if (ex1Code === 'SH') {
      ex1Contract = 'SHORT';
    } else if (ex1Code === 'SP') {
      ex1Contract = 'SPOT';
    }

    // Extract second contract type (EX2: 3 chars after EX1 code+digit)
    const ex2Code = strategyUpper.substring(offset + 3, offset + 5);
    if (ex2Code === 'LO') {
      ex2Contract = 'LONG';
    } else if (ex2Code === 'SH') {
      ex2Contract = 'SHORT';
    } else if (ex2Code === 'SP') {
      ex2Contract = 'SPOT';
    }

    return [ex1Contract, ex2Contract];
  };

  const formatProfit = (profit) => {
    if (profit === null || profit === undefined) return '-';
    const value = (Number(profit) * 100).toFixed(6);
    return `${value}%`;
  };

  const getProfitColor = (profit) => {
    if (profit === null || profit === undefined || isNaN(Number(profit))) {
      return '#ffffff';
    }
    const numProfit = Number(profit);
    if (numProfit > 0) {
      return '#3fb950'; // green for positive
    } else if (numProfit < 0) {
      return '#f85149'; // red for negative
    } else {
      return '#ffffff'; // white for zero
    }
  };

  // Check if opportunity timestamp is older than the configured limit
  const isOpportunityTooOld = (timestamp) => {
    if (!timestamp) return false;
    const oppDate = new Date(timestamp);
    const now = new Date();
    const ageInMinutes = (now - oppDate) / (1000 * 60); // Convert milliseconds to minutes
    return ageInMinutes > OPPORTUNITY_AGE_LIMIT_MINUTES;
  };

  const handleCreateTradeFromOpportunity = (opportunity) => {
    // Parse strategy to extract contract types
    // Strategy format: XX1YY2 where XX1 is ex1 contract, YY2 is ex2 contract
    // LO=LONG, SH=SHORT, SP=SPOT
    let ex1Contract = 'SPOT';
    let ex2Contract = 'SPOT';

    if (opportunity.strategy) {
      [ex1Contract, ex2Contract] = getContractTypesFromStrategy(opportunity.strategy);
    }

    // Build URL with prefilled data
    const params = new URLSearchParams({
      symbol: opportunity.symbol || '',
      ex1: opportunity.ex1_name || '',
      ex2: opportunity.ex2_name || '',
      ex1Contract: ex1Contract,
      ex2Contract: ex2Contract
    });

    // Get the base URL of the current application
    const baseUrl = window.location.origin;
    const tradesUrl = `${baseUrl}/trades?${params.toString()}`;

    // Open in a new window/tab with a specific name
    // If a window with name 'TradesWindow' already exists, it will reuse it
    window.open(tradesUrl, 'TradesWindow');
  };

  // Handle checkbox toggle for individual opportunity
  const handleToggleSelectOpportunity = (groupId, e) => {
    e.stopPropagation(); // Prevent row click
    setSelectedOpportunityIds(prev => {
      if (prev.includes(groupId)) {
        return prev.filter(id => id !== groupId);
      } else {
        return [...prev, groupId];
      }
    });
  };

  // Handle select/deselect all opportunities
  const handleSelectAllOpportunities = (e) => {
    if (e.target.checked) {
      // Select all opportunity groups
      setSelectedOpportunityIds(filteredOpportunities.map(group => group.groupId));

      // Track select all
      trackEvent('Opportunities', 'Select All', `Selected ${filteredOpportunities.length} opportunities`);
    } else {
      // Deselect all
      setSelectedOpportunityIds([]);

      // Track deselect all
      trackEvent('Opportunities', 'Deselect All', 'Cleared selection');
    }
  };

  // Handle bulk action dropdown change
  const handleBulkActionChange = (e) => {
    const action = e.target.value;
    setBulkAction(action);

    // Track bulk action selection
    if (action) {
      trackEvent('Opportunities', 'Bulk Action Selected', action);
    }
  };

  // Execute the selected bulk action
  const handleExecuteBulkAction = async () => {
    if (selectedOpportunityIds.length === 0) {
      alert('Please select at least one opportunity.');
      return;
    }

    if (!bulkAction) {
      alert('Please select an action.');
      return;
    }

    if (bulkAction === 'create_trade') {
      // Confirm bulk trade creation
      const confirmCreate = window.confirm(
        `Are you sure you want to create ${selectedOpportunityIds.length} trade(s) from the selected opportunities?`
      );

      if (!confirmCreate) {
        return;
      }

      try {
        // First, fetch symbols and exchanges to get IDs
        const symbolsResponse = await fetch(`${API_BASE_URL}/api/symbols/active`);
        const symbolsData = await symbolsResponse.json();
        const symbols = symbolsData.success ? symbolsData.data : [];

        const exchangesResponse = await fetch(`${API_BASE_URL}/api/exchanges/active`);
        const exchangesData = await exchangesResponse.json();
        const exchanges = exchangesData.success ? exchangesData.data : [];

        // Create trades sequentially
        let successCount = 0;
        let failCount = 0;
        const errors = [];

        for (const groupId of selectedOpportunityIds) {
          try {
            // Find the opportunity group
            const group = filteredOpportunities.find(g => g.groupId === groupId);
            if (!group || !group.newest) {
              failCount++;
              errors.push(`Group ${groupId}: Opportunity not found`);
              continue;
            }

            const opportunity = group.newest;

            // Parse strategy to extract contract types
            let ex1Contract = 'SPOT';
            let ex2Contract = 'SPOT';

            if (opportunity.strategy) {
              [ex1Contract, ex2Contract] = getContractTypesFromStrategy(opportunity.strategy);
            }

            // Find matching symbol and exchanges
            const matchingSymbol = symbols.find(s =>
              s.symbol && s.symbol.toLowerCase() === (opportunity.symbol || '').toLowerCase()
            );

            const matchingEx1 = exchanges.find(e =>
              e.exchange_name && e.exchange_name.toLowerCase() === (opportunity.ex1_name || '').toLowerCase()
            );

            const matchingEx2 = exchanges.find(e =>
              e.exchange_name && e.exchange_name.toLowerCase() === (opportunity.ex2_name || '').toLowerCase()
            );

            if (!matchingSymbol || !matchingEx1 || !matchingEx2) {
              failCount++;
              errors.push(`Group ${groupId}: Missing symbol or exchange data`);
              continue;
            }

            // Use formatted final profit as trade name
            const tradeName = formatProfit(opportunity.final_profit);

            // Create trade
            const tradeResponse = await fetch(`${API_BASE_URL}/api/trades`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                name: tradeName,
                symbolId: matchingSymbol.id.toString(),
                exchange1Id: matchingEx1.id.toString(),
                exchange1Contract: ex1Contract,
                exchange2Id: matchingEx2.id.toString(),
                exchange2Contract: ex2Contract,
                notes: `Created from opportunity ${groupId}`
              })
            });

            const tradeData = await tradeResponse.json();

            if (tradeData.success) {
              successCount++;
              console.log(`Trade created successfully from opportunity ${groupId}`);
            } else {
              failCount++;
              errors.push(`Group ${groupId}: ${tradeData.error || 'Unknown error'}`);
            }
          } catch (error) {
            failCount++;
            errors.push(`Group ${groupId}: ${error.message}`);
            console.error(`Error creating trade from opportunity ${groupId}:`, error);
          }
        }

        // Show result
        let message = `Successfully created ${successCount} trade(s).`;
        if (failCount > 0) {
          message += `\nFailed to create ${failCount} trade(s).`;
          if (errors.length > 0) {
            message += `\n\nErrors:\n${errors.slice(0, 5).join('\n')}`;
            if (errors.length > 5) {
              message += `\n... and ${errors.length - 5} more errors`;
            }
          }
        }

        // Track bulk action execution
        trackEvent('Opportunities', 'Bulk Action Executed', `Create Trade - ${successCount} success, ${failCount} failed`);

        alert(message);

        // Clear selections
        setSelectedOpportunityIds([]);
        setBulkAction('');
      } catch (error) {
        console.error('Error executing bulk create trade:', error);

        // Track bulk action error
        trackEvent('Opportunities', 'Bulk Action Error', `Create Trade - ${error.message}`);

        alert(`Error executing bulk create trade: ${error.message}`);
      }
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <SEO
          title="Arbitrage opportunities - calculated thousands combinations across multiple exchanges"
          description="Opportunities are calculated every moment, thousands of combinations are analyzed to find the best opportunities, including additional data supporting trading decisions."
          ogImage="https://micropincher.com/images/og-image.jpg"
          ogUrl="https://micropincher.com/opportunities"
          twitterImage="https://micropincher.com/images/og-image.jpg"
          canonicalUrl="https://micropincher.com/opportunities"
        />
        Loading opportunities data...
      </div>
    );
  }

  return (
    <div className="page-content">
      <SEO
        title="Arbitrage opportunities - calculated thousands combinations across multiple exchanges"
        description="Opportunities are calculated every moment, thousands of combinations are analyzed to find the best opportunities, including additional data supporting trading decisions."
        ogImage="https://micropincher.com/images/og-image.jpg"
        ogUrl="https://micropincher.com/opportunities"
        twitterImage="https://micropincher.com/images/og-image.jpg"
        canonicalUrl="https://micropincher.com/opportunities"
      />
      {error && (
        <div className="error" style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px' }}>
          {error}
        </div>
      )}

      <div className="filters" style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#1a1b23', border: '1px solid #2a2d3a', borderRadius: '4px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <label style={{ color: '#8b949e', fontSize: '10px', minWidth: '80px', marginRight: '10px' }}>Age (days):</label>
            <input
              type="number"
              value={ageFilter}
              onChange={(e) => setAgeFilter(e.target.value)}
              placeholder="7"
              min="1"
              style={{
                padding: '4px 8px',
                fontSize: '10px',
                backgroundColor: '#21262d',
                border: '1px solid #2a2d3a',
                borderRadius: '3px',
                color: '#ffffff',
                width: '200px'
              }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <label style={{ color: '#8b949e', fontSize: '10px', minWidth: '80px', marginRight: '10px' }}>Strategy:</label>
            <select
              value={filters.strategy}
              onChange={(e) => handleFilterChange('strategy', e.target.value)}
              style={{
                padding: '4px 8px',
                fontSize: '10px',
                backgroundColor: '#21262d',
                border: '1px solid #2a2d3a',
                borderRadius: '3px',
                color: '#ffffff',
                width: '200px'
              }}
            >
              {getUniqueStrategies().map((strategy, index) => (
                <option key={index} value={strategy}>
                  {strategy || 'All Strategies'}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <label style={{ color: '#8b949e', fontSize: '10px', minWidth: '80px', marginRight: '10px' }}>Symbol:</label>
            <input
              type="text"
              value={filters.symbol}
              onChange={(e) => handleFilterChange('symbol', e.target.value)}
              placeholder="Filter by symbol"
              style={{
                padding: '4px 8px',
                fontSize: '10px',
                backgroundColor: '#21262d',
                border: '1px solid #2a2d3a',
                borderRadius: '3px',
                color: '#ffffff',
                width: '200px'
              }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <label style={{ color: '#8b949e', fontSize: '10px', minWidth: '80px', marginRight: '10px' }}>Exchange 1:</label>
            <select
              value={filters.ex1}
              onChange={(e) => handleFilterChange('ex1', e.target.value)}
              style={{
                padding: '4px 8px',
                fontSize: '10px',
                backgroundColor: '#21262d',
                border: '1px solid #2a2d3a',
                borderRadius: '3px',
                color: '#ffffff',
                width: '200px'
              }}
            >
              {getUniqueExchanges().map((exchange, index) => (
                <option key={index} value={exchange}>
                  {exchange || 'All Exchanges'}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <label style={{ color: '#8b949e', fontSize: '10px', minWidth: '80px', marginRight: '10px' }}>Exchange 2:</label>
            <select
              value={filters.ex2}
              onChange={(e) => handleFilterChange('ex2', e.target.value)}
              style={{
                padding: '4px 8px',
                fontSize: '10px',
                backgroundColor: '#21262d',
                border: '1px solid #2a2d3a',
                borderRadius: '3px',
                color: '#ffffff',
                width: '200px'
              }}
            >
              {getUniqueExchanges().map((exchange, index) => (
                <option key={index} value={exchange}>
                  {exchange || 'All Exchanges'}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <label style={{ color: '#8b949e', fontSize: '10px', minWidth: '80px', marginRight: '10px' }}>Final Profit:</label>
            <select
              value={profitFilter.operator}
              onChange={(e) => setProfitFilter(prev => ({ ...prev, operator: e.target.value }))}
              style={{
                padding: '4px 8px',
                fontSize: '10px',
                backgroundColor: '#21262d',
                border: '1px solid #2a2d3a',
                borderRadius: '3px',
                color: '#ffffff',
                width: '90px',
                marginRight: '5px'
              }}
            >
              <option value="higher">Higher than</option>
              <option value="lower">Lower than</option>
            </select>
            <input
              type="text"
              value={profitFilter.value}
              onChange={(e) => setProfitFilter(prev => ({ ...prev, value: e.target.value }))}
              placeholder="0"
              style={{
                padding: '4px 8px',
                fontSize: '10px',
                backgroundColor: '#21262d',
                border: '1px solid #2a2d3a',
                borderRadius: '3px',
                color: '#ffffff',
                width: '100px'
              }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <label style={{ color: '#8b949e', fontSize: '10px', minWidth: '80px', marginRight: '10px' }}>Sort:</label>
            <select
              value={sortBy.column}
              onChange={(e) => setSortBy(prev => ({ ...prev, column: e.target.value }))}
              style={{
                padding: '4px 8px',
                fontSize: '10px',
                backgroundColor: '#21262d',
                border: '1px solid #2a2d3a',
                borderRadius: '3px',
                color: '#ffffff',
                width: '120px',
                marginRight: '5px'
              }}
            >
              <option value="strategy">Strategy</option>
              <option value="symbol">Symbol</option>
              <option value="final_profit">Final Profit</option>
              <option value="timestamp">Timestamp</option>
              <option value="ex1_name">Exchange 1</option>
              <option value="ex2_name">Exchange 2</option>
            </select>
            <select
              value={sortBy.direction}
              onChange={(e) => setSortBy(prev => ({ ...prev, direction: e.target.value }))}
              style={{
                padding: '4px 8px',
                fontSize: '10px',
                backgroundColor: '#21262d',
                border: '1px solid #2a2d3a',
                borderRadius: '3px',
                color: '#ffffff',
                width: '70px'
              }}
            >
              <option value="1-9">9→1</option>
              <option value="9-1">1→9</option>
            </select>
          </div>
          <div style={{ marginTop: '5px', display: 'flex', gap: '10px' }}>
            <button
              onClick={setBestOpportunitiesPreset}
              style={{
                padding: '4px 12px',
                fontSize: '10px',
                backgroundColor: '#3fb950',
                border: 'none',
                borderRadius: '3px',
                color: '#ffffff',
                cursor: 'pointer'
              }}
            >
              Best Opportunities
            </button>
            <button
              onClick={clearFilters}
              style={{
                padding: '4px 12px',
                fontSize: '10px',
                backgroundColor: '#f85149',
                border: 'none',
                borderRadius: '3px',
                color: '#ffffff',
                cursor: 'pointer'
              }}
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Actions Control */}
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: '8px' }}>
        <label htmlFor="bulk-action-select-opportunities" style={{ color: '#8b949e', fontSize: '14px' }}>
          Selected ({selectedOpportunityIds.length}):
        </label>
        <select
          id="bulk-action-select-opportunities"
          value={bulkAction}
          onChange={handleBulkActionChange}
          style={{
            padding: '6px 12px',
            borderRadius: '6px',
            border: '1px solid #30363d',
            background: '#0d1117',
            color: '#c9d1d9',
            fontSize: '14px',
            cursor: 'pointer'
          }}
        >
          <option value="">-- Select Action --</option>
          <option value="create_trade">Create Trade</option>
        </select>
        <button
          onClick={handleExecuteBulkAction}
          disabled={selectedOpportunityIds.length === 0 || !bulkAction}
          style={{
            padding: '6px 16px',
            borderRadius: '6px',
            border: 'none',
            background: (selectedOpportunityIds.length === 0 || !bulkAction) ? '#2a2d3a' : '#3fb950',
            color: (selectedOpportunityIds.length === 0 || !bulkAction) ? '#6e7681' : '#ffffff',
            fontSize: '14px',
            cursor: (selectedOpportunityIds.length === 0 || !bulkAction) ? 'not-allowed' : 'pointer',
            opacity: (selectedOpportunityIds.length === 0 || !bulkAction) ? 0.5 : 1
          }}
        >
          Apply
        </button>
      </div>

      <div className="data-table">
        <table>
          <thead>
            <tr>
              <th style={{ width: '30px' }} className="hide-mobile"></th>
              <th style={{ width: '30px', textAlign: 'center' }}>
                <input
                  type="checkbox"
                  checked={selectedOpportunityIds.length === filteredOpportunities.length && filteredOpportunities.length > 0}
                  onChange={handleSelectAllOpportunities}
                  onClick={(e) => e.stopPropagation()}
                />
              </th>
              <th>Strategy</th>
              <th>Final<br/>Profit</th>
              <th style={{ width: '30px' }} className="hide-mobile"></th>
              <th>Symbol</th>
              <th>Exchange 1</th>
              <th className="hide-mobile">TAKER FEE<br/>MAKER FEE<br/>Ex1</th>
              <th className="hide-mobile">Funding Rate<br/>Ex1</th>
              <th className="hide-mobile">Funding Time<br/>EX1</th>
              <th>Exchange 2</th>
              <th className="hide-mobile">TAKER FEE<br/>MAKER FEE<br/>Ex2</th>
              <th className="hide-mobile">Funding Rate<br/>Ex2</th>
              <th className="hide-mobile">Funding Time<br/>EX2</th>
              <th className="hide-mobile">GROUP ID<br/>RECORD ID</th>
              <th className="hide-mobile">Last<br/>Update</th>
            </tr>
          </thead>
          <tbody>
            {filteredOpportunities.map((group) => {
              // Safety check in case data structure is not as expected
              if (!group || !group.records || !group.newest) {
                return null;
              }

              const isExpanded = expandedGroups.has(group.groupId);
              const hasMultipleRecords = group.records.length > 1;
              const isTooOld = isOpportunityTooOld(group.newest.timestamp);
              const textColor = isTooOld ? '#6e7681' : 'inherit'; // Grey color if too old

              return (
                <React.Fragment key={group.groupId}>
                  {/* Main row - newest record */}
                  <tr
                    onClick={isMobileView ? () => toggleMobileRow(group.groupId) : undefined}
                    style={isMobileView ? { cursor: 'pointer' } : {}}
                    className="opportunity-row"
                  >
                    <td style={{ textAlign: 'center', padding: '2px', color: textColor }} className="hide-mobile">
                      {hasMultipleRecords && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpandGroup(group.groupId);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#3fb950',
                            cursor: 'pointer',
                            fontSize: '14px',
                            padding: '0',
                            width: '20px',
                            height: '20px'
                          }}
                          title={isExpanded ? 'Collapse' : `Expand (${group.records.length - 1} older records)`}
                        >
                          {isExpanded ? '▼' : '▶'}
                        </button>
                      )}
                    </td>
                    <td style={{ textAlign: 'center', padding: '4px' }}>
                      <input
                        type="checkbox"
                        checked={selectedOpportunityIds.includes(group.groupId)}
                        onChange={(e) => handleToggleSelectOpportunity(group.groupId, e)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td style={{ color: textColor }}>{group.newest.strategy || '-'}</td>
                    <td style={{ color: isTooOld ? textColor : getProfitColor(group.newest.final_profit) }}>
                      {formatProfit(group.newest.final_profit)}
                    </td>
                    <td style={{ textAlign: 'center', padding: '2px' }} className="hide-mobile">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCreateTradeFromOpportunity(group.newest);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '0',
                          width: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        title="Create trade from this opportunity"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          style={{ transition: 'all 0.2s' }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.1)';
                            e.currentTarget.querySelectorAll('path, circle').forEach(el => {
                              el.style.stroke = '#58a6ff';
                              el.style.fill = '#58a6ff';
                            });
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.querySelectorAll('path, circle').forEach(el => {
                              el.style.stroke = '#8b949e';
                              el.style.fill = '#8b949e';
                            });
                          }}
                        >
                          {/* Plus/Add icon */}
                          <circle cx="8" cy="8" r="7" stroke="#8b949e" strokeWidth="1.5" fill="none" style={{ transition: 'all 0.2s' }} />
                          <path d="M8 4.5V11.5M4.5 8H11.5" stroke="#8b949e" strokeWidth="1.5" strokeLinecap="round" style={{ transition: 'all 0.2s' }} />
                        </svg>
                      </button>
                    </td>
                    <td style={{ color: textColor }}>{group.newest.symbol?.toUpperCase() || '-'}</td>
                    <td style={{ padding: '2px 4px', lineHeight: '1.2', verticalAlign: 'top' }}>
                      <div style={{ fontSize: '11px' }}>
                        <a
                          href={buildTradingViewUrl(group.newest.ex1_name, group.newest.symbol, getContractTypesFromStrategy(group.newest.strategy)[0])}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: textColor, textDecoration: 'underline', cursor: 'pointer' }}
                        >
                          {group.newest.ex1_name || '-'}
                        </a>
                      </div>
                      <div style={{ color: textColor, fontSize: '11px' }}>
                        {getContractTypesFromStrategy(group.newest.strategy)[0]}
                      </div>
                    </td>
                    <td style={{ padding: '2px 4px', lineHeight: '1.2', verticalAlign: 'top' }} className="hide-mobile">
                      <div style={{ color: textColor, fontSize: '11px' }}>
                        {formatRate(group.newest.takerfee_ex1)}
                      </div>
                      <div style={{ color: textColor, fontSize: '11px' }}>
                        {formatRate(group.newest.makerfee_ex1)}
                      </div>
                    </td>
                    <td style={{ color: isTooOld ? textColor : getProfitColor(group.newest.funding_rate_ex1) }} className="hide-mobile">
                      {formatRate(group.newest.funding_rate_ex1)}
                    </td>
                    <td style={{ padding: '2px 4px', lineHeight: '1.2' }} className="hide-mobile">
                      <div style={{ color: isTooOld ? textColor : '#8b949e' }}>
                        {formatNextFundingTime(getNextFundingTime(group.newest.next_funding_time_ex1))}
                      </div>
                      <div style={{ color: isTooOld ? textColor : formatCountdown(getNextFundingTime(group.newest.next_funding_time_ex1)).color }}>
                        {formatCountdown(getNextFundingTime(group.newest.next_funding_time_ex1)).text}
                      </div>
                    </td>
                    <td style={{ padding: '2px 4px', lineHeight: '1.2', verticalAlign: 'top' }}>
                      <div style={{ fontSize: '11px' }}>
                        <a
                          href={buildTradingViewUrl(group.newest.ex2_name, group.newest.symbol, getContractTypesFromStrategy(group.newest.strategy)[1])}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: textColor, textDecoration: 'underline', cursor: 'pointer' }}
                        >
                          {group.newest.ex2_name || '-'}
                        </a>
                      </div>
                      <div style={{ color: textColor, fontSize: '11px' }}>
                        {getContractTypesFromStrategy(group.newest.strategy)[1]}
                      </div>
                    </td>
                    <td style={{ padding: '2px 4px', lineHeight: '1.2', verticalAlign: 'top' }} className="hide-mobile">
                      <div style={{ color: textColor, fontSize: '11px' }}>
                        {formatRate(group.newest.takerfee_ex2)}
                      </div>
                      <div style={{ color: textColor, fontSize: '11px' }}>
                        {formatRate(group.newest.makerfee_ex2)}
                      </div>
                    </td>
                    <td style={{ color: isTooOld ? textColor : getProfitColor(group.newest.funding_rate_ex2) }} className="hide-mobile">
                      {formatRate(group.newest.funding_rate_ex2)}
                    </td>
                    <td style={{ padding: '2px 4px', lineHeight: '1.2' }} className="hide-mobile">
                      <div style={{ color: isTooOld ? textColor : '#8b949e' }}>
                        {formatNextFundingTime(getNextFundingTime(group.newest.next_funding_time_ex2))}
                      </div>
                      <div style={{ color: isTooOld ? textColor : formatCountdown(getNextFundingTime(group.newest.next_funding_time_ex2)).color }}>
                        {formatCountdown(getNextFundingTime(group.newest.next_funding_time_ex2)).text}
                      </div>
                    </td>
                    <td style={{ padding: '2px 4px', lineHeight: '1.2', verticalAlign: 'top' }} className="hide-mobile">
                      <div style={{ color: textColor, fontSize: '11px' }}>
                        {group.newest.group_id || '-'}
                      </div>
                      <div style={{ color: textColor, fontSize: '11px' }}>
                        {group.newest.id || '-'}
                      </div>
                    </td>
                    <td style={{ padding: '2px 4px', lineHeight: '1.2', verticalAlign: 'top' }} className="hide-mobile">
                      <div style={{ color: textColor, fontSize: '11px' }}>
                        {group.newest.timestamp ? new Date(group.newest.timestamp).toLocaleDateString('en-GB') : '-'}
                      </div>
                      <div style={{ color: textColor, fontSize: '11px' }}>
                        {group.newest.timestamp ? new Date(group.newest.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }) : '-'}
                      </div>
                    </td>
                  </tr>

                  {/* Mobile details expansion */}
                  {isMobileView && expandedMobileRow === group.groupId && (
                    <tr className="mobile-details">
                      <td colSpan="6" style={{ padding: '12px', backgroundColor: '#161b22' }}>
                        <pre style={{
                          fontFamily: 'monospace',
                          fontSize: '11px',
                          margin: 0,
                          whiteSpace: 'pre',
                          color: textColor,
                          lineHeight: '1.4'
                        }}>{`${(group.newest.ex1_name || 'EXCHANGE1').toUpperCase()} (exchange 1):
${'-'.repeat(20)}
Taker fee:    ${formatRate(group.newest.takerfee_ex1).padStart(10)}
Maker fee:    ${formatRate(group.newest.makerfee_ex1).padStart(10)}
Funding rate: ${formatRate(group.newest.funding_rate_ex1).padStart(10)}
Next funding: ${formatNextFundingTime(getNextFundingTime(group.newest.next_funding_time_ex1))}
              ${formatCountdown(getNextFundingTime(group.newest.next_funding_time_ex1)).text}

${(group.newest.ex2_name || 'EXCHANGE2').toUpperCase()} (exchange 2):
${'-'.repeat(20)}
Taker fee:    ${formatRate(group.newest.takerfee_ex2).padStart(10)}
Maker fee:    ${formatRate(group.newest.makerfee_ex2).padStart(10)}
Funding rate: ${formatRate(group.newest.funding_rate_ex2).padStart(10)}
Next funding: ${formatNextFundingTime(getNextFundingTime(group.newest.next_funding_time_ex2))}
              ${formatCountdown(getNextFundingTime(group.newest.next_funding_time_ex2)).text}

Group ID:  ${group.newest.group_id || '-'}
Record ID: ${group.newest.id || '-'}
Last Update: ${group.newest.timestamp ? (() => {
  const date = new Date(group.newest.timestamp);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}`;
})() : '-'}`}</pre>
                      </td>
                    </tr>
                  )}

                  {/* Expanded rows - older records */}
                  {isExpanded && group.records.slice(1).map((opp, index) => {
                    const isOppTooOld = isOpportunityTooOld(opp.timestamp);
                    const oppTextColor = isOppTooOld ? '#6e7681' : 'inherit';

                    return (
                    <tr
                      key={opp.id}
                      style={{
                        backgroundColor: '#0d1117',
                        opacity: 0.8
                      }}
                    >
                      <td style={{ textAlign: 'center', paddingLeft: '20px' }}>
                        <span style={{ color: '#6e7681', fontSize: '10px' }}>└</span>
                      </td>
                      <td></td>
                      <td style={{ color: oppTextColor }}>{opp.strategy || '-'}</td>
                      <td style={{ color: isOppTooOld ? oppTextColor : getProfitColor(opp.final_profit) }}>
                        {formatProfit(opp.final_profit)}
                      </td>
                      <td></td>
                      <td style={{ color: oppTextColor }}>{opp.symbol?.toUpperCase() || '-'}</td>
                      <td style={{ padding: '2px 4px', lineHeight: '1.2', verticalAlign: 'top' }}>
                        <div style={{ fontSize: '11px' }}>
                          <a
                            href={buildTradingViewUrl(opp.ex1_name, opp.symbol, getContractTypesFromStrategy(opp.strategy)[0])}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: oppTextColor, textDecoration: 'underline', cursor: 'pointer' }}
                          >
                            {opp.ex1_name || '-'}
                          </a>
                        </div>
                        <div style={{ color: oppTextColor, fontSize: '11px' }}>
                          {getContractTypesFromStrategy(opp.strategy)[0]}
                        </div>
                      </td>
                      <td style={{ padding: '2px 4px', lineHeight: '1.2', verticalAlign: 'top' }}>
                        <div style={{ color: oppTextColor, fontSize: '11px' }}>
                          {formatRate(opp.takerfee_ex1)}
                        </div>
                        <div style={{ color: oppTextColor, fontSize: '11px' }}>
                          {formatRate(opp.makerfee_ex1)}
                        </div>
                      </td>
                      <td style={{ color: isOppTooOld ? oppTextColor : getProfitColor(opp.funding_rate_ex1) }}>
                        {formatRate(opp.funding_rate_ex1)}
                      </td>
                      <td style={{ padding: '2px 4px', lineHeight: '1.2' }}>
                        <div style={{ color: isOppTooOld ? oppTextColor : '#8b949e' }}>
                          {formatNextFundingTime(getNextFundingTime(opp.next_funding_time_ex1))}
                        </div>
                        <div style={{ color: isOppTooOld ? oppTextColor : formatCountdown(getNextFundingTime(opp.next_funding_time_ex1)).color }}>
                          {formatCountdown(getNextFundingTime(opp.next_funding_time_ex1)).text}
                        </div>
                      </td>
                      <td style={{ padding: '2px 4px', lineHeight: '1.2', verticalAlign: 'top' }}>
                        <div style={{ fontSize: '11px' }}>
                          <a
                            href={buildTradingViewUrl(opp.ex2_name, opp.symbol, getContractTypesFromStrategy(opp.strategy)[1])}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: oppTextColor, textDecoration: 'underline', cursor: 'pointer' }}
                          >
                            {opp.ex2_name || '-'}
                          </a>
                        </div>
                        <div style={{ color: oppTextColor, fontSize: '11px' }}>
                          {getContractTypesFromStrategy(opp.strategy)[1]}
                        </div>
                      </td>
                      <td style={{ padding: '2px 4px', lineHeight: '1.2', verticalAlign: 'top' }}>
                        <div style={{ color: oppTextColor, fontSize: '11px' }}>
                          {formatRate(opp.takerfee_ex2)}
                        </div>
                        <div style={{ color: oppTextColor, fontSize: '11px' }}>
                          {formatRate(opp.makerfee_ex2)}
                        </div>
                      </td>
                      <td style={{ color: isOppTooOld ? oppTextColor : getProfitColor(opp.funding_rate_ex2) }}>
                        {formatRate(opp.funding_rate_ex2)}
                      </td>
                      <td style={{ padding: '2px 4px', lineHeight: '1.2' }}>
                        <div style={{ color: isOppTooOld ? oppTextColor : '#8b949e' }}>
                          {formatNextFundingTime(getNextFundingTime(opp.next_funding_time_ex2))}
                        </div>
                        <div style={{ color: isOppTooOld ? oppTextColor : formatCountdown(getNextFundingTime(opp.next_funding_time_ex2)).color }}>
                          {formatCountdown(getNextFundingTime(opp.next_funding_time_ex2)).text}
                        </div>
                      </td>
                      <td style={{ padding: '2px 4px', lineHeight: '1.2', verticalAlign: 'top' }}>
                        <div style={{ color: oppTextColor, fontSize: '11px' }}>
                          {opp.group_id || '-'}
                        </div>
                        <div style={{ color: oppTextColor, fontSize: '11px' }}>
                          {opp.id || '-'}
                        </div>
                      </td>
                      <td style={{ padding: '2px 4px', lineHeight: '1.2', verticalAlign: 'top' }}>
                        <div style={{ color: oppTextColor, fontSize: '11px' }}>
                          {opp.timestamp ? new Date(opp.timestamp).toLocaleDateString('en-GB') : '-'}
                        </div>
                        <div style={{ color: oppTextColor, fontSize: '11px' }}>
                          {opp.timestamp ? new Date(opp.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }) : '-'}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ color: '#8b949e', fontSize: '11px' }}>
          Showing: {filteredOpportunities.length} opportunity groups ({totalRecords} total records) of {totalGroups} groups in database
          {pageSize !== 'unlimited' && ` (Page ${currentPage} of ${Math.ceil(totalGroups / pageSize) || 1})`}
        </div>

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <label style={{ color: '#8b949e', fontSize: '10px' }}>Groups per page:</label>
          <select
            value={pageSize}
            onChange={(e) => {
              const newSize = e.target.value === 'unlimited' ? 'unlimited' : parseInt(e.target.value);
              setPageSize(newSize);
              setCurrentPage(1);
            }}
            style={{
              padding: '4px 8px',
              fontSize: '10px',
              backgroundColor: '#21262d',
              border: '1px solid #2a2d3a',
              borderRadius: '3px',
              color: '#ffffff',
              width: '100px'
            }}
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
            <option value={500}>500</option>
            <option value="unlimited">Unlimited</option>
          </select>
        </div>
      </div>

      {pageSize !== 'unlimited' && totalGroups > 0 && (
        <div style={{ marginTop: '15px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
          <button
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            style={{
              padding: '4px 12px',
              fontSize: '10px',
              backgroundColor: currentPage === 1 ? '#2a2d3a' : '#3fb950',
              border: 'none',
              borderRadius: '3px',
              color: currentPage === 1 ? '#6e7681' : '#ffffff',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
            }}
          >
            First
          </button>
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            style={{
              padding: '4px 12px',
              fontSize: '10px',
              backgroundColor: currentPage === 1 ? '#2a2d3a' : '#3fb950',
              border: 'none',
              borderRadius: '3px',
              color: currentPage === 1 ? '#6e7681' : '#ffffff',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
            }}
          >
            Previous
          </button>
          <span style={{ color: '#8b949e', fontSize: '10px' }}>
            Page {currentPage} of {Math.ceil(totalGroups / pageSize) || 1}
          </span>
          <button
            onClick={() => setCurrentPage(prev => prev + 1)}
            disabled={currentPage >= Math.ceil(totalGroups / pageSize)}
            style={{
              padding: '4px 12px',
              fontSize: '10px',
              backgroundColor: currentPage >= Math.ceil(totalGroups / pageSize) ? '#2a2d3a' : '#3fb950',
              border: 'none',
              borderRadius: '3px',
              color: currentPage >= Math.ceil(totalGroups / pageSize) ? '#6e7681' : '#ffffff',
              cursor: currentPage >= Math.ceil(totalGroups / pageSize) ? 'not-allowed' : 'pointer'
            }}
          >
            Next
          </button>
          <button
            onClick={() => setCurrentPage(Math.ceil(totalGroups / pageSize))}
            disabled={currentPage >= Math.ceil(totalGroups / pageSize)}
            style={{
              padding: '4px 12px',
              fontSize: '10px',
              backgroundColor: currentPage >= Math.ceil(totalGroups / pageSize) ? '#2a2d3a' : '#3fb950',
              border: 'none',
              borderRadius: '3px',
              color: currentPage >= Math.ceil(totalGroups / pageSize) ? '#6e7681' : '#ffffff',
              cursor: currentPage >= Math.ceil(totalGroups / pageSize) ? 'not-allowed' : 'pointer'
            }}
          >
            Last
          </button>
        </div>
      )}
    </div>
  );
};

export default Opportunities;